/* eslint-disable consistent-return */
import ReactECharts from 'echarts-for-react'
import echarts from 'echarts/lib/echarts'
import { isUndefined, max, maxBy, min, minBy, round, uniqBy } from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'
import React, { useRef, useState } from 'react'
import i18n from 'simple-react-i18n'
import { getDate, getFullDate } from 'utils/DateUtil'
import { exportFile } from 'utils/ExportDataUtil'
import {
    exportExcelIcon,
    exportPictureIcon, getAxisIntervalFormatter, logIcon, setLogOptions, setYOptions, toEchartsData, yAutomaticScaleValues,
} from '../../../../../components/echart/EChartUtils'
import { shallowEqual, useSelector } from 'react-redux'
import { getColorFromPalette, getThresholdColor } from 'utils/ColorUtil'
import { renderToString } from 'react-dom/server'
import { IconStatus } from 'utils/StatusUtil'

const DEFAULT_GRAPH_HEIGHT = 150
const DEFAULT_GAP = 50
const DEFAULT_HEADER_HEIGHT = 40
const DEFAULT_FOOTER_HEIGHT = 75

const HydroChart = ({
    dataMeasures = [],
    startDate,
    endDate,

    headerHeight = DEFAULT_HEADER_HEIGHT,
    footerHeight = DEFAULT_FOOTER_HEIGHT,
}) => {
    const {
        hydrometricStations,
        hydrometryThresholds,
        hydrometryDataTypes,
    } = useSelector(store => ({
        hydrometricStations: store.HydrometryReducer.hydrometricStations,
        hydrometryThresholds: store.HydrometryReducer.hydrometryThresholds,
        hydrometryDataTypes: store.HydrometryReducer.hydrometryDataTypes,
    }), shallowEqual)

    // use on mouse event
    let echartRef = useRef(undefined)
    let displayToolbox = useRef(false)

    const [displayLog, setDisplayLog] = useState(false)

    const minDate = startDate ?? min(dataMeasures.map(d => minBy(d.measures, 'date')?.date).filter(d => !isUndefined(d)))
    const maxDate = endDate ?? max(dataMeasures.map(d => maxBy(d.measures, 'date')?.date).filter(d => !isUndefined(d)))

    const {
        formatter,
        interval,
    } = getAxisIntervalFormatter(moment(maxDate), moment(minDate))

    const listDataTypes = uniqBy(dataMeasures, 'dataType').map(m => m.dataType)

    const series = listDataTypes.flatMap((dt, i) => {
        const dataType = hydrometryDataTypes.find(p => p.id === dt)
        const measures = dataMeasures.filter(m => m.dataType === dt)
        return measures.map((m, mi) => {
            const hydro = hydrometricStations.find(p => p.id === m.stationId)
            const thresholds = hydrometryThresholds.filter(t => !isUndefined(t.value) && t.stationId == m.stationId && t.dataType == m.dataType).map(t => ({
                value: t.value,
                title: t.title,
                color: t.htmlColor || getThresholdColor(t.color),
            }))
            return {
                name: `${hydro?.name ?? ''} (${dataType?.label ?? ''})`,
                type: 'line',
                data: toEchartsData(displayLog ? m.measures.filter(d => d.value > 0) : m.measures),
                markLine: {
                    silent: false,
                    data: thresholds.map(t => ({
                        yAxis: t.value,
                        symbol: 'none',
                        label: {
                            show: true,
                            position: 'middle',
                            formatter: () => t.title ? `${t.title} : ${round(t.value, 5)}` : '',
                        },
                        lineStyle: {
                            normal: {
                                color: t.color,
                                type: 'dashed',
                            },
                        },
                    })),
                },

                connectNulls: false,
                yAxisIndex: i,
                xAxisIndex: i,
                color: getColorFromPalette(mi),
                lineStyle: {
                    type: dataType?.lineType ?? 'solid',
                    opacity: dataType?.lineOpacity ?? 1,
                    width: dataType?.lineWidth ?? 2,
                },
            }
        })
    })

    const grids = listDataTypes.map((dt, i) => {
        const dataType = hydrometryDataTypes.find(p => p.id === dt)
        return {
            id: dt,
            name: dataType ? `${dataType.label ?? ''} ${dataType.unit ? `[${dataType.unit}]` : ''}` : i18n.unknown,
            gridOptions: {
                top: i === 0 ? 0 : undefined,
            },
        }
    })

    let tmpGap = headerHeight
    const grid = listDataTypes.map((_, i) => {
        const tmpTop = tmpGap + (i === 0 ? 0 : DEFAULT_GAP)
        const tmpGrid = {
            top: tmpTop,
            height: DEFAULT_GRAPH_HEIGHT,
            left: '70px',
            right: '40px',
        }
        tmpGap = tmpTop + DEFAULT_GRAPH_HEIGHT
        return tmpGrid
    })

    const xAxis = listDataTypes.map((_, i) => {
        return {
            type: 'time',
            boundaryGap: true,
            axisLabel: {
                rotate: 0,
                formatter,
                show: true,
            },
            axisLine: { show: true },
            axisTick: { show: true },
            splitLine: { show: true },
            interval,
            gridIndex: i,
            min: minDate,
            max: maxDate,
        }
    })

    const yAxis = grids.map((dt, index) => {
        const dataType = hydrometryDataTypes.find(p => p.id === dt.id)
        const filteredData = series.filter(d => d.yAxisIndex === index)
        const allMeasures = filteredData.flatMap(d => d.data?.map(m => m.value[1]) ?? [])
        const allThresholds = filteredData.flatMap(d => d.markLine.data?.map(t => t.yAxis) ?? [])
        const yScale = yAutomaticScaleValues([...allMeasures, ...allThresholds])
        return {
            type: displayLog ? 'log' : 'value',
            nameLocation: 'middle',
            name: dataType ? `${dataType.label ?? ''} ${dataType.unit ? `[${dataType.unit}]` : ''}` : i18n.unknown,
            gridIndex: index,
            nameGap: 40,
            showSplitLine: true,
            ...setYOptions(null, yScale),
            ...setLogOptions(displayLog),
        }
    })

    const getLegend = () => ({
        top: 10,
        left: 70,
        right: displayToolbox.current ? `${30 * 4 + 35}px` : 40,
        type: 'scroll',
        show: true,
    })

    const getToolbox = () => ({
        top: 5,
        right: 40,
        showTitle: false,
        itemSize: 18,
        tooltip: {
            show: true,
            position: 'bottom',
        },
        feature: displayToolbox.current ? {
            myLog: {
                title: i18n.logarithm,
                icon: logIcon,
                onclick: () => setDisplayLog(p => !p),
            },
            saveAsImage: {
                title: i18n.pictureExport,
                icon: exportPictureIcon,
                name: i18n.dataHydro,
            },
            myToolExport: {
                title: i18n.excelExport,
                icon: exportExcelIcon,
                onclick: () => {
                    const exportData = dataMeasures.flatMap(dm => {
                        const hydro = hydrometricStations.find(({ id }) => id === dm.stationId)
                        const dt = hydrometryDataTypes.find(h => h.id === dm.dataType)
                        return dm.measures.map(m => ({
                            code: hydro?.code,
                            date: { value: getDate(m.date), format: 'dd/MM/yyyy', cellType: 'date' },
                            value: { value: m.value, format: '0.00000', cellType: 'number' },
                            name: hydro?.name || dm.stationId,
                            dataType: dt?.name ?? i18n.unknown,
                        }))
                    })
                    exportFile({
                        data: exportData.length ? [
                            {
                                ...exportData[0],
                                headers: ['code', 'name', 'dataType', 'date', 'value'],
                            },
                            ...exportData.slice(1),
                        ] : [],
                        exportType: 'xlsx',
                        titleFile: i18n.dataHydro,
                    }, true)
                },
            },
            restore: { title: i18n.restore },
        } : {},
    })

    const options = {
        series,
        legend: getLegend(),
        xAxis,
        yAxis,
        tooltip: {
            trigger: 'axis',
            formatter: params => {
                const date = getFullDate(params[0].value[0])
                const result = params.map(o => `${o.marker} ${o.seriesName} : ${round(o.value[1], 2)} ${o.unit ?? ''}`).join('<br />')
                return `${date} <br /> ${result}`
            },
        },
        grid,
        dataZoom: [{
            type: 'slider',
            xAxisIndex: grids.map((_, index) => index),
            filterMode: 'none',
        }, {
            bottom: '10px',
            type: 'inside',
            filterMode: 'filter',
            xAxisIndex: grids.map((_, index) => index),
            handleStyle: {
                color: '#fff',
                shadowBlur: 3,
                shadowColor: 'rgba(0, 0, 0, 0.6)',
                shadowOffsetX: 2,
                shadowOffsetY: 2,
            },
        }],
        toolbox: getToolbox(),
        backgroundColor: '#fff',
    }

    const componentHeight = grids.reduce((acc, { gridOptions: { height = DEFAULT_GRAPH_HEIGHT, top = DEFAULT_GAP } = {} }) => acc + height + top, headerHeight + footerHeight)

    return (
        <div
            onMouseOver={() => {
                displayToolbox.current = true
                echartRef.current?.getEchartsInstance().setOption({
                    ...options,
                    legend: getLegend(),
                    toolbox: getToolbox(),
                }, true, true)
            }}
            onMouseOut={() => {
                displayToolbox.current = false
                echartRef.current?.getEchartsInstance().setOption({
                    ...options,
                    legend: getLegend(),
                    toolbox: getToolbox(),
                }, true, true)
            }}
        >
            <ReactECharts
                echarts={echarts}
                option={options}
                notMerge={true}
                lazyUpdate={true}
                style={{ height: componentHeight }}
                ref={e => {
                    echartRef.current = e
                }}
            />
        </div>
    )
}

HydroChart.propTypes = {
    dataMeasures: PropTypes.arrayOf(PropTypes.shape({

    })),
    startDate: PropTypes.number,
    endDate: PropTypes.number,

    headerHeight: PropTypes.number,
    footerHeight: PropTypes.number,
}

export default HydroChart